import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import './TestimonialsSection.css';

interface Testimonial {
  id: number;
  name: string;
  role: string;
  rating: number;
  text: string;
}

const TestimonialsSection: React.FC = () => {
  const navigate = useNavigate();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  // Generate a simple set of testimonials with realistic, human-like language.
  const testimonials: Testimonial[] = [
    {
      id: 1,
      name: "Sarah Johnson",
      role: "Marketing Manager",
      rating: 5,
      text: "I used to skip workouts because I never knew what to do. With TaaFit, my coach built a clear plan and guided me on training and lifestyle. In just four months, I’m down 18 lbs, have more energy, and I actually look forward to training."
    },
    {
      id: 2,
      name: "Mike Chen",
      role: "Software Engineer",
      rating: 5,
      text: "I work 50–60 hours a week at a desk. The short, guided sessions and personalized scheduling kept me consistent—120 days in a row and counting. My back feels better and my focus at work improved."
    },
    {
      id: 3,
      name: "Emily Rodriguez",
      role: "Fitness Enthusiast",
      rating: 5,
      text: "Having a professional coach take over training and nutrition is a game-changer. I added 40 lbs to my deadlift in 10 weeks and finally feel confident walking into the gym with a plan."
    },
    {
      id: 4,
      name: "David Thompson",
      role: "Business Owner",
      rating: 5,
      text: "I travel twice a month and used to lose consistency. Having an online coach feels like always having a personal trainer with me. With his guidance on training and nutrition, I’ve stayed on track and dropped a belt size—without ever feeling restricted."
    },
    {
      id: 5,
      name: "Lisa Wang",
      role: "Teacher",
      rating: 5,
      text: "The 20‑minute morning sessions fit perfectly between classes and commute. I’m sleeping better, my mood is steadier, and my students even noticed I have more energy."
    },
    {
      id: 6,
      name: "Alex Martinez",
      role: "Student",
      rating: 5,
      text: "On a student budget I needed something simple. The Coach gave me an easy-to-follow plan and guided me through my diet. After 12 weeks I’m stronger, more consistent, and I noticed that I don't feel as anxious in social situations anymore."
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  const getInitials = (fullName: string) => {
    const parts = fullName.trim().split(/\s+/);
    const first = parts[0]?.[0] ?? '';
    const last = parts[parts.length - 1]?.[0] ?? '';
    return (first + last).toUpperCase();
  };

  const getAvatarColor = (seed: string) => {
    // Deterministic color pick from a small palette based on a simple hash of the name
    const palette = [
      '#0ea5e9', // sky-500
      '#10b981', // emerald-500
      '#8b5cf6', // violet-500
      '#f59e0b', // amber-500
      '#ef4444', // red-500
      '#14b8a6', // teal-500
    ];
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      hash = (hash << 5) - hash + seed.charCodeAt(i);
      hash |= 0; // Convert to 32bit integer
    }
    const index = Math.abs(hash) % palette.length;
    return palette[index];
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, index) => (
      <span 
        key={index} 
        className={`star ${index < rating ? 'filled' : ''}`}
      >
        ★
      </span>
    ));
  };

  return (
    <section id="testimonials-section" className="testimonials-section">
      <div className="testimonials-container">
        <motion.div
          ref={ref}
          className="testimonials-content"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
        >
          <motion.div className="testimonials-header" variants={itemVariants}>
            <h2 className="testimonials-title">What Our Clients Say</h2>
            <p className="testimonials-subtitle">
              Live a life that YOU deserve. Start your transformation now! <br />
              Your success story could be next!
            </p>
          </motion.div>

          <motion.div className="testimonials-grid" variants={containerVariants}>
            {testimonials.map((testimonial) => (
              <motion.div
                key={testimonial.id}
                className="testimonial-card"
                variants={itemVariants}
                whileHover={{ 
                  y: -10,
                  boxShadow: "0 25px 50px rgba(0, 0, 0, 0.1)"
                }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="testimonial-content">
                  <div className="testimonial-rating">
                    {renderStars(testimonial.rating)}
                  </div>
                  
                  <p className="testimonial-text">"{testimonial.text}"</p>
                  
                  <div className="testimonial-author">
                    <div
                      className="author-avatar"
                      style={{ backgroundColor: getAvatarColor(testimonial.name), color: '#fff', fontWeight: 700 }}
                      aria-hidden="true"
                    >
                      {getInitials(testimonial.name)}
                    </div>
                    <div className="author-info">
                      <h4 className="author-name">{testimonial.name}</h4>
                      <p className="author-role">{testimonial.role}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div className="testimonials-stats" variants={itemVariants}>
            <div className="stat">
              <span className="stat-number">90+</span>
              <span className="stat-label">Satisfied Clients</span>
            </div>
            <div className="stat">
              <span className="stat-number">100%</span>
              <span className="stat-label">Success Rate</span>
            </div>
            <div className="stat">
              <span className="stat-number">24/7</span>
              <span className="stat-label">Support</span>
            </div>
          </motion.div>

          <motion.div className="cta-section" variants={itemVariants}>
            <h3 className="cta-title">Ready to Start Your Transformation?</h3>
            <p className="cta-text">
              Join our community of successful fitness enthusiasts and begin your journey today.
            </p>
            <button 
              className="cta-button"
              onClick={() => navigate('/professionals')}
                          >
              Get a plan
            </button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
